// src/components/SendReminder/SendReminder.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { sendReminderAPI } from '../../services/sendReminderService';
import { parseApiError } from '../../utils/apiError';
import { 
  ArrowLeftIcon,
  EnvelopeIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';

const SendReminder = () => {
  const navigate = useNavigate();
  const { surveyId } = useParams();
  const { isSuperAdmin, isCompanyAdmin, isAuthenticated } = useAuth();
  
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [selectedSurvey, setSelectedSurvey] = useState(surveyId || '');
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [reminderResults, setReminderResults] = useState(null);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedInvitations, setSelectedInvitations] = useState([]);
  const [showCompleted, setShowCompleted] = useState(false);
  
  const [surveyDropdownOpen, setSurveyDropdownOpen] = useState(false);
  const surveyDropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (surveyDropdownRef.current && !surveyDropdownRef.current.contains(event.target)) {
        setSurveyDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Load initial data
  useEffect(() => {
    if (isAuthenticated) {
      loadSurveys();
    }
  }, [isAuthenticated]);

  // Load pending invitations when survey changes
  useEffect(() => {
    if (selectedSurvey) {
      loadPendingInvitations(selectedSurvey);
    } else {
      setPendingInvitations([]);
    }
  }, [selectedSurvey]);

  const loadSurveys = async () => {
    try {
      const response = await sendReminderAPI.getSurveys();
      setSurveys(response.data);
      
      // If surveyId is provided in URL, set it as selected
      if (surveyId && !selectedSurvey) {
        setSelectedSurvey(surveyId);
      }
    } catch (err) {
      console.error('Error loading surveys:', err);
      setError('Failed to load surveys');
    }
  };

  const loadPendingInvitations = async (surveyId) => {
    setLoading(true);
    try {
      const response = await sendReminderAPI.getPendingInvitations(surveyId);
      setPendingInvitations(response.data);
      setSelectedInvitations([]);
      setError('');
    } catch (err) {
      console.error('Error loading pending invitations:', err);
      setError('Failed to load pending invitations');
      setPendingInvitations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSendReminder = async (invitationId = null) => {
    setSending(true);
    setError('');
    setSuccess('');
    setReminderResults(null);

    try {
      let result;
      
      if (invitationId) {
        // Send reminder to specific invitation
        result = await sendReminderAPI.sendReminder(invitationId);
      } else if (selectedInvitations.length > 0) {
        // Send reminders to selected invitations
        const promises = selectedInvitations.map(id => sendReminderAPI.sendReminder(id));
        const results = await Promise.allSettled(promises);
        
        result = {
          data: {
            totalRemindersSent: results.filter(r => r.status === 'fulfilled' && r.value.data.totalRemindersSent > 0).length,
            totalFailed: results.filter(r => r.status === 'rejected' || r.value.data.totalFailed > 0).length,
            details: results.map((r, index) => {
              if (r.status === 'fulfilled') {
                return r.value.data.details[0]; // Each call returns one detail
              } else {
                const invitation = pendingInvitations.find(inv => inv.id === selectedInvitations[index]);
                return {
                  invitationId: selectedInvitations[index],
                  employeeName: invitation?.employeeName || 'Unknown',
                  email: invitation?.email || 'Unknown',
                  success: false,
                  message: parseApiError(r.reason, 'Failed to send reminder').message
                };
              }
            })
          }
        };
      } else {
        // Send reminders to all pending for the survey
        result = await sendReminderAPI.sendRemindersForSurvey(selectedSurvey);
      }

      setReminderResults(result.data);
      
      if (result.data.totalRemindersSent > 0) {
        setSuccess(`Reminders sent successfully! ${result.data.totalRemindersSent} sent, ${result.data.totalFailed} failed`);
        
        // Reload pending invitations to update reminder counts
        if (selectedSurvey) {
          loadPendingInvitations(selectedSurvey);
        }
        
        // Clear selections
        setSelectedInvitations([]);
      } else {
        setError('No reminders were sent successfully');
      }
    } catch (err) {
      console.error('Error sending reminders:', err);
      setError(parseApiError(err, 'Failed to send reminders').message);
    } finally {
      setSending(false);
    }
  };

  const handleSendAllReminders = async () => {
    if (!selectedSurvey) {
      setError('Please select a survey first');
      return;
    }
    
    await handleSendReminder();
  };

  const handleInvitationSelect = (invitationId) => {
    setSelectedInvitations(prev => 
      prev.includes(invitationId)
        ? prev.filter(id => id !== invitationId)
        : [...prev, invitationId]
    );
  };

  const handleSelectAll = () => {
    setSelectedInvitations(
      selectedInvitations.length === filteredInvitations.length
        ? []
        : filteredInvitations.map(inv => inv.id)
    );
  };

  const handleBackToSurveys = () => {
    navigate('/surveys');
  };

  const clearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  const getSelectedSurveyName = () => {
    return surveys.find(s => s.id === parseInt(selectedSurvey))?.name || 'Select Survey';
  };

  // Filter and search logic
  const filteredInvitations = useMemo(() => {
    let filtered = pendingInvitations;

    if (!showCompleted) {
      filtered = filtered.filter(inv => !inv.isCompleted);
    }

    if (searchTerm) {
      filtered = filtered.filter(inv =>
        inv.employeeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inv.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  }, [pendingInvitations, searchTerm, showCompleted]);

  // Pagination logic
  const totalItems = filteredInvitations.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentInvitations = filteredInvitations.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const getReminderStatus = (invitation) => {
    if (invitation.isCompleted) {
      return { status: 'completed', text: 'Completed', color: 'text-green-600 bg-green-100' };
    }
    
    if (invitation.reminderCount === 0) {
      return { status: 'pending', text: 'No reminders sent', color: 'text-gray-600 bg-gray-100' };
    }
    
    if (invitation.reminderCount >= 10) {
      return { status: 'max', text: 'Max reminders reached', color: 'text-red-600 bg-red-100' };
    }
    
    return { 
      status: 'reminded', 
      text: `${invitation.reminderCount} reminder${invitation.reminderCount > 1 ? 's' : ''} sent`,
      color: 'text-blue-600 bg-blue-100'
    };
  };

  const getDaysAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (!isAuthenticated) {
    return (
      <div className="p-6 w-full bg-gradient-to-br from-blue-50 via-white to-blue-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900">Please log in to send reminders</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 w-full bg-gradient-to-br from-blue-50 via-white to-blue-50 min-h-screen">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={handleBackToSurveys}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Back to Surveys"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Send Survey Reminders
              </h1>
              <p className="text-gray-600">
                Send reminder emails to employees who haven't completed their surveys
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <span className="bg-blue-100 text-blue-800 px-3 py-2 rounded-full font-medium text-sm">
              {selectedInvitations.length} Selected
            </span>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-2xl p-4">
          <div className="flex items-center">
            <XCircleIcon className="h-5 w-5 text-red-400 mr-3" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Success Alert */}
      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-2xl p-4">
          <div className="flex items-center">
            <CheckCircleIcon className="h-5 w-5 text-green-400 mr-3" />
            <p className="text-sm text-green-700">{success}</p>
          </div>
        </div>
      )}

      {/* Survey Selection Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <ChartBarIcon className="w-5 h-5 text-blue-500" />
          Select Survey
        </h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-2">
            <div className="relative" ref={surveyDropdownRef}>
              <button
                onClick={() => setSurveyDropdownOpen(!surveyDropdownOpen)}
                className="w-full px-4 py-3 text-left border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors flex items-center justify-between"
              >
                <span className="truncate">{getSelectedSurveyName()}</span>
                <ChevronDownIcon className={`w-4 h-4 transition-transform ${surveyDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {surveyDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                  {surveys.map((survey) => (
                    <button
                      key={survey.id}
                      onClick={() => {
                        setSelectedSurvey(survey.id.toString());
                        setSurveyDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                        selectedSurvey === survey.id.toString() ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                      }`}
                    >
                      <div className="font-medium">{survey.name}</div>
                      <div className="text-sm text-gray-500 mt-1">{survey.description}</div>
                      <div className="text-xs text-gray-400 mt-1">
                        Company: {survey.companyName}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleSendAllReminders}
              disabled={sending || !selectedSurvey || filteredInvitations.length === 0}
              className="flex-1 px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-sm flex items-center gap-2 transition-colors justify-center"
            >
              {sending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Sending...
                </>
              ) : (
                <>
                  <EnvelopeIcon className="w-5 h-5" />
                  Send All Reminders
                </>
              )}
            </button>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowCompleted(!showCompleted)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                showCompleted 
                  ? 'bg-blue-500 text-white border-blue-500' 
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {showCompleted ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
              {showCompleted ? 'Hide Completed' : 'Show Completed'}
            </button>
          </div>
        </div>

        {selectedSurvey && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="text-gray-600">Total Invitations</div>
              <div className="text-xl font-semibold text-gray-900">{pendingInvitations.length}</div>
            </div>
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="text-blue-600">Pending Completion</div>
              <div className="text-xl font-semibold text-blue-900">
                {pendingInvitations.filter(inv => !inv.isCompleted).length}
              </div>
            </div>
            <div className="bg-orange-50 p-3 rounded-lg">
              <div className="text-orange-600">Reminders Sent</div>
              <div className="text-xl font-semibold text-orange-900">
                {pendingInvitations.reduce((sum, inv) => sum + inv.reminderCount, 0)}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Pending Invitations Card */}
      {selectedSurvey && (
        <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-6 mb-6">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <UserGroupIcon className="w-5 h-5 text-blue-500" />
                Pending Survey Invitations ({selectedInvitations.length} selected)
              </h2>
              <p className="text-sm text-gray-600">
                {filteredInvitations.length} of {pendingInvitations.length} invitations
                {searchTerm && ` matching "${searchTerm}"`}
                {showCompleted && ' (including completed)'}
              </p>
            </div>

            <div className="flex gap-3 w-full lg:w-auto">
              {/* Search Input */}
              <div className="relative flex-1 lg:flex-none lg:w-80">
                <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input 
                  type="text" 
                  placeholder="Search employees by name or email..." 
                  value={searchTerm} 
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                />
                {searchTerm && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                )}
              </div>

              <button
                onClick={handleSelectAll}
                disabled={filteredInvitations.length === 0}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors whitespace-nowrap"
              >
                {selectedInvitations.length === filteredInvitations.length ? 'Deselect All' : 'Select All'}
              </button>

              {selectedInvitations.length > 0 && (
                <button
                  onClick={() => handleSendReminder()}
                  disabled={sending}
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors whitespace-nowrap flex items-center gap-2"
                >
                  <EnvelopeIcon className="w-4 h-4" />
                  Send Selected ({selectedInvitations.length})
                </button>
              )}
            </div>
          </div>

          {/* Invitations List */}
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <span className="ml-3 text-gray-600">Loading invitations...</span>
            </div>
          ) : filteredInvitations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <UserGroupIcon className="w-16 h-16 text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {selectedSurvey ? 'No invitations found' : 'Select a survey to view invitations'}
              </h3>
              <p className="text-gray-500 mb-4">
                {searchTerm 
                  ? 'Try adjusting your search terms' 
                  : selectedSurvey 
                    ? 'All employees have completed this survey or no invitations found'
                    : 'Choose a survey from the dropdown above'
                }
              </p>
              {searchTerm && (
                <button
                  onClick={clearSearch}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Clear search
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {currentInvitations.map((invitation) => {
                const status = getReminderStatus(invitation);
                const daysAgo = getDaysAgo(invitation.sentDate);
                
                return (
                  <div 
                    key={invitation.id}
                    className={`border rounded-lg transition-all duration-200 ${
                      selectedInvitations.includes(invitation.id)
                        ? 'border-blue-500 bg-blue-50 shadow-sm'
                        : invitation.isCompleted
                        ? 'border-green-200 bg-green-50'
                        : 'border-gray-200 bg-white hover:shadow-md'
                    }`}
                  >
                    <div className="p-4 flex items-center gap-4">
                      {!invitation.isCompleted && (
                        <input
                          type="checkbox"
                          checked={selectedInvitations.includes(invitation.id)}
                          onChange={() => handleInvitationSelect(invitation.id)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      )}
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                            {invitation.employeeName?.split(' ').map(n => n[0]).join('').toUpperCase()}
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">
                              {invitation.employeeName}
                            </h3>
                            <p className="text-sm text-gray-600">{invitation.email}</p>
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <ClockIcon className="w-4 h-4" />
                            Sent {daysAgo} day{daysAgo !== 1 ? 's' : ''} ago
                          </span>
                          
                          {invitation.lastReminderSent && (
                            <span className="flex items-center gap-1">
                              <EnvelopeIcon className="w-4 h-4" />
                              Last reminder: {getDaysAgo(invitation.lastReminderSent)} day{getDaysAgo(invitation.lastReminderSent) !== 1 ? 's' : ''} ago
                            </span>
                          )}
                          
                          <span className={`px-2 py-1 rounded-full font-medium ${status.color}`}>
                            {status.text}
                          </span>

                          {invitation.nextReminderDate && !invitation.isCompleted && (
                            <span className="flex items-center gap-1 text-orange-600">
                              <ExclamationTriangleIcon className="w-4 h-4" />
                              Next reminder: {new Date(invitation.nextReminderDate).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>

                      {!invitation.isCompleted && invitation.reminderCount < 10 && (
                        <button
                          onClick={() => handleSendReminder(invitation.id)}
                          disabled={sending}
                          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap"
                        >
                          <EnvelopeIcon className="w-4 h-4" />
                          Send Reminder
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {filteredInvitations.length > 0 && (
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6 pt-6 border-t border-gray-200">
              <div className="text-sm text-gray-700">
                Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} invitations
              </div>

              <div className="flex items-center gap-4">
                <div className="flex gap-1">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    <ChevronLeftIcon className="w-4 h-4" />
                  </button>
                  
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(page => 
                      page === 1 || 
                      page === totalPages || 
                      Math.abs(page - currentPage) <= 1
                    )
                    .map((page, index, array) => {
                      const showEllipsis = index < array.length - 1 && array[index + 1] - page > 1;
                      return (
                        <React.Fragment key={page}>
                          <button
                            onClick={() => handlePageChange(page)}
                            className={`px-3 py-1 text-sm border rounded transition-colors ${
                              currentPage === page
                                ? 'bg-blue-500 text-white border-blue-500'
                                : 'border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                          {showEllipsis && (
                            <span className="px-2 text-gray-500">...</span>
                          )}
                        </React.Fragment>
                      );
                    })}
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    <ChevronRightIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Reminder Results */}
      {reminderResults && (
        <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Reminder Results</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <div className="text-green-600 text-sm font-medium">Successfully Sent</div>
              <div className="text-2xl font-bold text-green-900">{reminderResults.totalRemindersSent}</div>
            </div>
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <div className="text-red-600 text-sm font-medium">Failed</div>
              <div className="text-2xl font-bold text-red-900">{reminderResults.totalFailed}</div>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <div className="text-blue-600 text-sm font-medium">Total Attempted</div>
              <div className="text-2xl font-bold text-blue-900">
                {reminderResults.totalRemindersSent + reminderResults.totalFailed}
              </div>
            </div>
          </div>

          <div className="space-y-3 max-h-96 overflow-y-auto">
            {reminderResults.details.map((result, index) => (
              <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <div className="flex-1">
                  <div className="font-medium">{result.employeeName}</div>
                  <div className="text-sm text-gray-600">{result.email}</div>
                  {!result.success && (
                    <div className="text-sm text-red-600 mt-1">{result.message}</div>
                  )}
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  result.success 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {result.success ? 'Sent' : 'Failed'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SendReminder;